import { SagaMiddleware } from '@redux-saga/core';
import invariant from 'invariant';
import conformsTo from 'lodash/conformsTo';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import {
  Action,
  ActionFromReducersMapObject,
  CombinedState,
  Reducer,
  ReducersMapObject,
  StateFromReducersMapObject,
  Store,
} from 'redux';
import { InjectedSagaDescriptor } from './getSagaInjectors';

export type CombinedReducer<S1, A1 extends Action, S2, A2 extends Action> = Reducer<
  CombinedState<
    StateFromReducersMapObject<ReducersMapObject<S1, A1> & ReducersMapObject<S2, A2>>
  >,
  ActionFromReducersMapObject<ReducersMapObject<S1, A1> & ReducersMapObject<S2, A2>>
>;

export interface ExtendedStore<S = $TsFixMe, A extends Action = Action>
  extends Store<S, A> {
  runSaga: SagaMiddleware['run'];
  injectedReducers: ReducersMapObject;
  createReducer: <SR, AR extends Action>(
    injectedReducers?: ReducersMapObject<SR, AR>,
  ) => CombinedReducer<S, A, SR, AR>;
  injectedSagas: Record<string, InjectedSagaDescriptor>;
}

/**
 * Validate the shape of redux store
 *
 * @param {Store} store - a store object
 */
export function checkStore(store: unknown): asserts store is ExtendedStore {
  const shape = {
    dispatch: isFunction,
    subscribe: isFunction,
    getState: isFunction,
    replaceReducer: isFunction,
    runSaga: isFunction,
    injectedReducers: isObject,
    injectedSagas: isObject,
    createReducer: isFunction,
  };

  invariant(conformsTo(store, shape), '[Store] injectors: Expected a valid redux store');
}
